<script setup>
const { showEdit } = defineProps({
  showEdit: {
    type: Boolean,
    default() {
      return true;
    },
  },
});

const emits = defineEmits(["onChange"]);

const toast = useToast();

const confirm = useConfirm();
const isLoading = ref(false);
const { data: sessionData, getSession } = useAuth();

const userProfile = computed(() => {
  return sessionData?.value?.data?.agent_profile;
});

const academic = ref({
  qualification: null,
  certificate: null,
  institution_name: null,
  university_name: null,
  program_name: null, // both degree or diploma
  field_of_study: null,
  uni_start_date: null,
  uni_end_date: null,
  student_type: null,

  second_qualification: null,
  second_certificate: null,
  second_institution_name: null,
  second_university_name: null,
  second_program_name: null, // both degree or diploma
  second_field_of_study: null,
  second_uni_start_date: null,
  second_uni_end_date: null,
  second_student_type: null,

  third_qualification: null,
  third_certificate: null,
  third_institution_name: null,
  third_university_name: null,
  third_program_name: null, // both degree or diploma
  third_field_of_study: null,
  third_uni_start_date: null,
  third_uni_end_date: null,
  third_student_type: null,
});

const setAcademicPayload = (data, type) => {
  let payload = { ...academic.value };
  console.log("data: ", data);

  if (type === "first") {
    payload.qualification = data.qualification;
    payload.certificate = data.certificate;
    payload.institution_name = data.institution_name;
    payload.university_name = data.university_name;
    payload.program_name = data.programName;
    payload.field_of_study = data.fieldOfStudy;
    payload.uni_start_date = data.startDate;
    payload.uni_end_date = data.endDate;
    payload.student_type = data.student_type;
  } else if (type === "second") {
    payload.second_qualification = data.qualification;
    payload.second_certificate = data.certificate;
    payload.second_institution_name = data.institution_name;
    payload.second_university_name = data.university_name;
    payload.second_program_name = data.programName;
    payload.second_field_of_study = data.fieldOfStudy;
    payload.second_uni_start_date = data.startDate;
    payload.second_uni_end_date = data.endDate;
    payload.second_student_type = data.student_type;
  } else if (type === "third") {
    payload.third_qualification = data.qualification;
    payload.third_certificate = data.certificate;
    payload.third_institution_name = data.institution_name;
    payload.third_university_name = data.university_name;
    payload.third_program_name = data.programName;
    payload.third_field_of_study = data.fieldOfStudy;
    payload.third_uni_start_date = data.startDate;
    payload.third_uni_end_date = data.endDate;
    payload.third_student_type = data.student_type;
  }

  return payload;
};

const confirmDelete = (type) => {
  confirm.require({
    header: "Delete confirmation",
    message: "Do you want to delete this academic info?",
    rejectProps: {
      label: "Cancel",
      severity: "secondary",
      outlined: true,
    },
    acceptProps: {
      label: "Delete",
      severity: "danger",
    },
    accept: () => {
      deleteAcademicPayload(type);
    },
  });
};

const deleteAcademicPayload = async (type) => {
  let payload = { ...academic.value };

  if (type === "first") {
    payload.qualification = null;
    payload.certificate = null;
    payload.institution_name = null;
    payload.university_name = null;
    payload.program_name = null;
    payload.field_of_study = null;
    payload.uni_start_date = null;
    payload.uni_end_date = null;
    payload.student_type = null;
  } else if (type === "second") {
    payload.second_qualification = null;
    payload.second_certificate = null;
    payload.second_institution_name = null;
    payload.second_university_name = null;
    payload.second_program_name = null;
    payload.second_field_of_study = null;
    payload.second_uni_start_date = null;
    payload.second_uni_end_date = null;
    payload.second_student_type = null;
  } else if (type === "third") {
    payload.third_qualification = null;
    payload.third_certificate = null;
    payload.third_institution_name = null;
    payload.third_university_name = null;
    payload.third_program_name = null;
    payload.third_field_of_study = null;
    payload.third_uni_start_date = null;
    payload.third_uni_end_date = null;
    payload.third_student_type = null;
  }

  academic.value = payload;
  console.log("Deleted academic record:", payload);
  await saveAcademic("delete");
};

const reloadAcademic = () => {
  const agent = sessionData.value?.data?.agent_profile;
  if (agent) academic.value = agent;
  console.log("Agent: ", agent);
};

const onSaveAcademicData = async (data, type) => {
  academic.value = setAcademicPayload(data, type);
  await saveAcademic();
};

const saveAcademic = async (action) => {
  isLoading.value = true;
  let message = "Academic information updated successfully.";

  switch (action) {
    case "create":
      message = "Academic information added.";
      break;
    case "update":
      message = "Academic information updated.";
      break;
    case "delete":
      message = "Academic information deleted.";
      break;
  }

  await useApiFetch(`profile/update/academic`, {
    method: "POST",
    body: academic.value,
    async onResponse(res) {
      if (res.response.status === 200) {
        toast.add({
          severity: "success",
          summary: message,
          life: 3000,
        });
      } else {
        toast.add({
          severity: "error",
          summary: "Something went wrong",
          life: 3000,
        });
      }
      await getSession();
    },
  });
  isLoading.value = false;
};

const onAddAcademic = async (data) => {
  const type = !(
    academic.value?.university_name ||
    academic.value?.institution_name ||
    academic.value?.certificate
  )
    ? "first"
    : !(
        academic.value?.second_university_name ||
        academic.value?.second_institution_name ||
        academic.value?.second_certificate
      )
    ? "second"
    : !(
        academic.value?.third_university_name ||
        academic.value?.third_institution_name ||
        academic.value?.third_certificate
      )
    ? "third"
    : null;

  if (!type) {
    toast.add({
      severity: "warn",
      summary: "Maximum of 3 academic records allowed.",
      life: 3000,
    });
    return;
  }

  academic.value = setAcademicPayload(data, type);
  await saveAcademic();
};

onMounted(() => {
  reloadAcademic();
});
</script>

<template>
  <div class="text-sm">
    <!-- First academic info -->
    <div class="flex justify-between gap-3" v-if="academic?.university_name">
      <div class="flex gap-3 mt-3">
        <Avatar :label="academic?.university_name?.at(0)" size="large" 
          class="shrink-0"/>
        <div class="max-w-96">
          <p class="text-gray-900 font-medium">
            {{ academic?.university_name }}
          </p>
          <div class="inline-flex gap-1 items-center">
            <p class="text-xs capitalize">
              <span class="font-semibold">{{ academic?.student_type }} student</span> <span>&#8226;</span> {{ academic?.program_name }} -
              {{ academic?.field_of_study }}
            </p>
            <!-- <p></p> -->
            <!-- <p class="text-xs">
              {{ academic?.program_name }} -
              {{ academic?.field_of_study }}
            </p> -->
          </div>
          <p class="text-xs text-muted">
            {{ academic?.uni_start_date }}-{{ academic?.uni_end_date }}
          </p>
        </div>
      </div>
      <div v-if="showEdit" class="flex item-end">
        <CreateOrEditAcademic
          :isEdit="true"
          :initialForm="{
            qualification: academic?.qualification,
            certificate: academic?.certificate,
            institution_name: academic?.institution_name,
            university_name: academic?.university_name,
            programName: academic?.program_name,
            fieldOfStudy: academic?.field_of_study,
            startDate: academic?.uni_start_date,
            endDate: academic?.uni_end_date,
            student_type: academic?.student_type,
          }"
          @onSave="(data) => onSaveAcademicData(data, 'first')"
        />
        <Button
          icon="pi pi-trash"
          severity="secondary"
          size="small"
          text
          @click="confirmDelete('first')"
        ></Button>
      </div>
    </div>
    <div
      class="flex justify-between items-start gap-3"
      v-if="academic?.institution_name"
    >
      <div class="flex gap-3 mt-3">
        <Avatar
          :label="academic?.institution_name?.at(0)"
          size="large"
          class="shrink-0"
        />
        <div class="max-w-96">
          <p class="text-gray-900 font-medium w-full break-words">
            {{ academic?.institution_name }}
          </p>
          <div class="inline-flex gap-1 items-center w-full break-words">
            <p class="text-xs w-full break-words">
              {{ academic?.program_name }} -
              {{ academic?.field_of_study }}
            </p>
          </div>
        </div>
      </div>
      <div v-if="showEdit">
        <CreateOrEditAcademic
          :isEdit="true"
          :initialForm="{
            qualification: academic?.qualification,
            certificate: academic?.certificate,
            institution_name: academic?.institution_name,
            university_name: academic?.university_name,
            programName: academic?.program_name,
            fieldOfStudy: academic?.field_of_study,
            startDate: academic?.uni_start_date,
            endDate: academic?.uni_end_date,
            student_type: academic?.student_type,
          }"
          @onSave="(data) => onSaveAcademicData(data, 'first')"
        />

        <Button
          icon="pi pi-trash"
          size="small"
          severity="secondary"
          text
          @click="confirmDelete('first')"
        ></Button>
      </div>
    </div>
    <div class="flex justify-between gap-3" v-if="academic?.certificate">
      <div class="flex gap-3 mt-3">
        <img
          src="~/assets/img/icon/school.png"
          width="50"
          height="50"
          class="bg-gray-100 p-2 rounded-md shrink-0 w-[50px] h-[50px]"
        />
        <div class="max-w-96">
          <p class="text-gray-900 font-medium w-full break-words">
            {{ academic?.qualification }}
          </p>
          <div class="inline-flex gap-1 items-center w-full break-words">
            <p class="text-xs font-semibold capitalize break-words w-full ">
              {{ academic?.certificate }}
            </p>
          </div>
        </div>
      </div>
      <div v-if="showEdit">
        <CreateOrEditAcademic
          :isEdit="true"
          :initialForm="{
            qualification: academic?.qualification,
            certificate: academic?.certificate,
            institution_name: academic?.institution_name,
            university_name: academic?.university_name,
            programName: academic?.program_name,
            fieldOfStudy: academic?.field_of_study,
            startDate: academic?.uni_start_date,
            endDate: academic?.uni_end_date,
            student_type: academic?.student_type,
          }"
          @onSave="(data) => onSaveAcademicData(data, 'first')"
        />
        <Button
          icon="pi pi-trash"
          severity="secondary"
          size="small"
          text
          @click="confirmDelete('first')"
        ></Button>
      </div>
    </div>

    <!-- Second academic info -->
    <div
      class="flex justify-between items-start gap-3"
      v-if="academic?.second_university_name"
    >
      <div class="flex gap-3 mt-3">
        <Avatar :label="academic?.second_university_name?.at(0)" size="large"
          class="shrink-0" />
        <div class="max-w-96">
          <p class="text-gray-900 font-medium">
            {{ academic?.second_university_name }}
          </p>
          <div class="inline-flex gap-1 items-center">
            <p class="text-xs capitalize">
              <span class="font-semibold">{{ academic?.second_student_type }} student </span> <span>&#8226;</span>  {{ academic?.second_program_name }} -
              {{ academic?.second_field_of_study }}
            </p>
            
            <!-- <p class="text-xs">
              {{ academic?.second_program_name }} -
              {{ academic?.second_field_of_study }}
            </p> -->
          </div>
          <p class="text-xs text-muted">
            {{ academic?.second_uni_start_date }}-{{
              academic?.second_uni_end_date
            }}
          </p>
        </div>
      </div>
      <div v-if="showEdit">
        <CreateOrEditAcademic
          :isEdit="true"
          :initialForm="{
            qualification: academic?.second_qualification,
            certificate: academic?.second_certificate,
            institution_name: academic?.second_institution_name,
            university_name: academic?.second_university_name,
            programName: academic?.second_program_name,
            fieldOfStudy: academic?.second_field_of_study,
            startDate: academic?.second_uni_start_date,
            endDate: academic?.second_uni_end_date,
            student_type: academic?.second_student_type,
          }"
          @onSave="(data) => onSaveAcademicData(data, 'second')"
        />

        <Button
          icon="pi pi-trash"
          size="small"
          severity="secondary"
          text
          @click="confirmDelete('second')"
        ></Button>
      </div>
    </div>
    <div
      class="flex justify-between items-start gap-3"
      v-if="academic?.second_institution_name"
    >
      <div class="flex gap-3 mt-3">
        <Avatar
          :label="academic?.second_institution_name?.at(0)"
          size="large"
          class="shrink-0"
        />
        <div class="max-w-96">
          <p class="text-gray-900 font-medium w-full break-words">
            {{ academic?.second_institution_name }}
          </p>
          <div class="inline-flex gap-1 items-center w-full break-words">
            <p class="text-xs w-full break-words">
              {{ academic?.second_program_name }} -
              {{ academic?.second_field_of_study }}
            </p>
          </div>
        </div>
      </div>
      <div v-if="showEdit">
        <CreateOrEditAcademic
          :isEdit="true"
          :initialForm="{
            qualification: academic?.second_qualification,
            certificate: academic?.second_certificate,
            institution_name: academic?.second_institution_name,
            university_name: academic?.second_university_name,
            programName: academic?.second_program_name,
            fieldOfStudy: academic?.second_field_of_study,
            startDate: academic?.second_uni_start_date,
            endDate: academic?.second_uni_end_date,
            student_type: academic?.second_student_type,
          }"
          @onSave="(data) => onSaveAcademicData(data, 'second')"
        />

        <Button
          icon="pi pi-trash"
          size="small"
          severity="secondary"
          text
          @click="confirmDelete('second')"
        ></Button>
      </div>
    </div>
    <div class="flex justify-between gap-3" v-if="academic?.second_certificate">
      <div class="flex gap-3 mt-3">
        <img
          src="~/assets/img/icon/school.png"
          width="50"
          height="50"
          class="bg-gray-100 p-2 rounded-md shrink-0 w-[50px] h-[50px]"
        />
        <div class="max-w-96">
          <p class="text-gray-900 font-medium w-full break-words">
            {{ academic?.second_qualification }}
          </p>
          <div class="inline-flex gap-1 items-center w-full break-words">
            <p class="text-xs font-semibold capitalize break-words w-full">
              {{ academic?.second_certificate }}
            </p>
          </div>
        </div>
      </div>
      <div v-if="showEdit">
        <CreateOrEditAcademic
          :isEdit="true"
          :initialForm="{
            qualification: academic?.second_qualification,
            certificate: academic?.second_certificate,
            institution_name: academic?.second_institution_name,
            university_name: academic?.second_university_name,
            programName: academic?.second_program_name,
            fieldOfStudy: academic?.second_field_of_study,
            startDate: academic?.second_uni_start_date,
            endDate: academic?.second_uni_end_date,
            student_type: academic?.second_student_type,
          }"
          @onSave="(data) => onSaveAcademicData(data, 'second')"
        />
        <Button
          icon="pi pi-trash"
          severity="secondary"
          size="small"
          text
          @click="confirmDelete('second')"
        ></Button>
      </div>
    </div>

    <!-- Third academic info -->
    <div
      class="flex justify-between items-start gap-3"
      v-if="academic?.third_university_name"
    >
      <div class="flex gap-3 mt-3">
        <Avatar
          :label="academic?.third_university_name?.at(0)"
          size="large"
          class="shrink-0"
        />
        <div class="max-w-96">
          <p class="text-gray-900 font-medium w-full break-words">
            {{ academic?.third_university_name }}
          </p>
          <div class="inline-flex gap-2 items-center w-full break-words">
            <p class="text-xs capitalize break-words">
              <span class="font-semibold">{{ academic?.third_student_type }} student</span> <span>&#8226;</span> {{ academic?.third_program_name }} -
              {{ academic?.third_field_of_study }}
            </p>
            <!-- <p></p>
            <p class="text-xs w-full break-words">
              {{ academic?.third_program_name }} -
              {{ academic?.third_field_of_study }}
            </p> -->
          </div>
          <p class="text-xs text-muted">
            {{ academic?.third_uni_start_date }}-{{
              academic?.third_uni_end_date
            }}
          </p>
        </div>
      </div>
      <div v-if="showEdit">
        <CreateOrEditAcademic
          :isEdit="true"
          :initialForm="{
            qualification: academic?.third_qualification,
            certificate: academic?.third_certificate,
            institution_name: academic?.third_institution_name,
            university_name: academic?.third_university_name,
            programName: academic?.third_program_name,
            fieldOfStudy: academic?.third_field_of_study,
            startDate: academic?.third_uni_start_date,
            endDate: academic?.third_uni_end_date,
            student_type: academic?.third_student_type,
          }"
          @onSave="(data) => onSaveAcademicData(data, 'third')"
        />
        <Button
          icon="pi pi-trash"
          size="small"
          severity="secondary"
          text
          @click="confirmDelete('third')"
        ></Button>
      </div>
    </div>
    <div
      class="flex justify-between items-start gap-3"
      v-if="academic?.third_institution_name"
    >
      <div class="flex gap-3 mt-3">
        <Avatar
          :label="academic?.third_institution_name?.at(0)"
          size="large"
          class="shrink-0"
        />
        <div class="max-w-96">
          <p class="text-gray-900 font-medium w-full break-words">
            {{ academic?.third_institution_name }}
          </p>
          <div class="inline-flex gap-1 items-center w-full break-words">
            <p class="text-xs w-full break-words">
              {{ academic?.third_program_name }} -
              {{ academic?.third_field_of_study }}
            </p>
          </div>
        </div>
      </div>
      <div v-if="showEdit">
        <CreateOrEditAcademic
          :isEdit="true"
          :initialForm="{
            qualification: academic?.third_qualification,
            certificate: academic?.third_certificate,
            institution_name: academic?.third_institution_name,
            university_name: academic?.third_university_name,
            programName: academic?.third_program_name,
            fieldOfStudy: academic?.third_field_of_study,
            startDate: academic?.third_uni_start_date,
            endDate: academic?.third_uni_end_date,
            student_type: academic?.third_student_type,
          }"
          @onSave="(data) => onSaveAcademicData(data, 'third')"
        />

        <Button
          icon="pi pi-trash"
          size="small"
          severity="secondary"
          text
          @click="confirmDelete('third')"
        ></Button>
      </div>
    </div>
    <div
      class="flex justify-between items-start gap-3"
      v-if="academic?.third_certificate"
    >
      <div class="flex gap-3 mt-3">
        <img
          src="~/assets/img/icon/school.png"
          width="50"
          height="50"
          class="bg-gray-100 p-2 rounded-md shrink-0 w-[50px] h-[50px]"
        />
        <div class="max-w-96">
          <p class="text-gray-900 font-medium w-full break-words">
            {{ academic?.third_qualification }}
          </p>
          <div class="inline-flex gap-1 items-center w-full break-words">
            <p class="text-xs font-semibold capitalize break-words w-full">
              {{ academic?.third_certificate }}
            </p>
          </div>
        </div>
      </div>
      <div v-if="showEdit">
        <CreateOrEditAcademic
          :isEdit="true"
          :initialForm="{
            qualification: academic?.third_qualification,
            certificate: academic?.third_certificate,
            institution_name: academic?.third_institution_name,
            university_name: academic?.third_university_name,
            programName: academic?.third_program_name,
            fieldOfStudy: academic?.third_field_of_study,
            startDate: academic?.third_uni_start_date,
            endDate: academic?.third_uni_end_date,
            student_type: academic?.third_student_type,
          }"
          @onSave="(data) => onSaveAcademicData(data, 'third')"
        />
        <Button
          icon="pi pi-trash"
          size="small"
          severity="secondary"
          text
          @click="confirmDelete('third')"
        ></Button>
      </div>
    </div>

    <div
      v-if="
        showEdit &&
        (!academic.qualification ||
          !academic.second_qualification ||
          !academic.third_qualification)
      "
      class="mt-3"
    >
      <CreateOrEditAcademic @onSave="onAddAcademic" />
    </div>
  </div>
</template>
